import { ReactElement, useMemo } from 'react';
import { Chip, Skeleton, Typography } from '@mui/material';

import { useGetMainTokenBalanceQuery } from 'modules/api/actions/getMainTokenBalance';
import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { useConnection } from 'modules/auth/hooks/useConnection';
import { BaseStat } from 'modules/common/components/BaseStat';
import { BorderedPaper } from 'modules/common/components/BorderedPaper';
import {
  BUY_MAIN_MORE_LINK,
  DEFAULT_DECIMAL_PLACES,
  HUNDRED,
  ZERO,
} from 'modules/common/const';
import { useGetUSDAmount } from 'modules/common/hooks/useGetUSDAmount';
import { useGlobalTranslation } from 'modules/i18n/hooks/useGlobalTranslation';
import { useGetAccountDelegateTotalStaked } from 'modules/pool/hooks/useGetAccountDelegateTotalStaked';
import { useGetPoolAPYs } from 'modules/pool/hooks/useGetPoolAPYs';

import buyMoreImage from '../../assets/buy-more.svg';
import { translation } from './translation';
import { useStyles } from './useStyles';

export function Stats(): ReactElement {
  const { classes, cx } = useStyles();
  const { t, keys } = useGlobalTranslation(translation);

  const { isConnected, connectionKey, cacheTime } = useConnection();
  const {
    data: mainTokenBalance = ZERO,
    isLoading: isMainTokenBalanceLoading,
  } = useGetMainTokenBalanceQuery(
    { connectionKey },
    {
      skip: !isConnected,
      selectFromResult: mapDataToUndefinedIfSkip,
      refetchOnMountOrArgChange: cacheTime,
    },
  );

  const { totalStakedAmount, isLoading: isAccountTotalStakedLoading } =
    useGetAccountDelegateTotalStaked();
  const { avgAPY, isLoading: isAPYLoading } = useGetPoolAPYs();

  const annualEarning = useMemo(
    () => totalStakedAmount.multipliedBy(avgAPY.dividedBy(HUNDRED)),
    [totalStakedAmount, avgAPY],
  );

  const totalStakedAmountUSD = useGetUSDAmount(totalStakedAmount);

  const annualEarningUSD = useGetUSDAmount(annualEarning);

  const mainTokenBalanceUSD = useGetUSDAmount(mainTokenBalance);

  return (
    <div className={classes.root}>
      <BorderedPaper className={cx(classes.card, classes.myStakedCard)}>
        <Typography alignSelf="center" fontWeight={600} variant="body1">
          {t(keys.myStaked, { token: t(keys.tokens.main) })}
        </Typography>

        <div className={classes.amount}>
          <Typography component="div" fontSize={36} fontWeight={600}>
            {isAccountTotalStakedLoading ? (
              <Skeleton />
            ) : (
              totalStakedAmount.decimalPlaces(DEFAULT_DECIMAL_PLACES).toFormat()
            )}
          </Typography>

          {totalStakedAmountUSD && !totalStakedAmountUSD.isZero() && (
            <Typography variant="body1">
              {t(keys.unit.$, {
                value: totalStakedAmountUSD.decimalPlaces(2).toFormat(),
              })}
            </Typography>
          )}
        </div>
      </BorderedPaper>

      <BaseStat
        label={t(keys.annualEarning, { token: t(keys.tokens.main) })}
        topRightContent={
          <Chip
            color="secondary"
            label={t(keys.apy, { apy: avgAPY.decimalPlaces(2).toFormat() })}
          />
        }
        usdValue={annualEarningUSD}
        value={
          isAccountTotalStakedLoading || isAPYLoading
            ? undefined
            : annualEarning
        }
      />

      <BaseStat
        label={t(keys.availableToStake, { token: t(keys.tokens.main) })}
        topRightContent={
          BUY_MAIN_MORE_LINK ? (
            <a
              className={classes.link}
              href={BUY_MAIN_MORE_LINK}
              rel="noreferrer"
              target="_blank"
            >
              <img alt={t(keys.buyMore)} src={buyMoreImage} />
            </a>
          ) : undefined
        }
        usdValue={mainTokenBalanceUSD}
        value={
          isMainTokenBalanceLoading
            ? undefined
            : mainTokenBalance.decimalPlaces(DEFAULT_DECIMAL_PLACES)
        }
      />
    </div>
  );
}
